/**
 * Toasts — confirmation that a desk action landed, and the place errors surface
 * when there is no inline slot for them.
 *
 * A module-level store deliberately: any mutation handler anywhere can call
 * `toast.success(...)` without threading a context through. It is created inside
 * `createRoot` so its reactivity is owned, not leaked.
 */
import { For, Show, createRoot, createSignal, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import CheckCircle from "lucide-solid/icons/circle-check";
import AlertCircle from "lucide-solid/icons/circle-alert";
import Info from "lucide-solid/icons/info";
import X from "lucide-solid/icons/x";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

export type ToastTone = "success" | "error" | "info";

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

const store = createRoot(() => {
  const [items, setItems] = createSignal<ToastItem[]>([]);
  let nextId = 1;

  const dismiss = (id: number) => setItems((list) => list.filter((t) => t.id !== id));

  const push = (tone: ToastTone, message: string) => {
    const id = nextId++;
    setItems((list) => [...list, { id, tone, message }]);
    // Errors linger — an operator may have looked away mid-action.
    window.setTimeout(() => dismiss(id), tone === "error" ? 8000 : 4000);
  };

  return { items, push, dismiss };
});

export const toast = {
  success: (message: string) => store.push("success", message),
  error: (message: string) => store.push("error", message),
  info: (message: string) => store.push("info", message),
};

const TONE: Record<ToastTone, { class: string; icon: () => JSX.Element }> = {
  success: {
    class: "border-[color:var(--c9-border)] text-[color:var(--c9-text-success)]",
    icon: () => <CheckCircle size={16} />,
  },
  error: {
    class: "border-[color:var(--c9-border-danger)] text-[color:var(--c9-text-danger)]",
    icon: () => <AlertCircle size={16} />,
  },
  info: {
    class: "border-[color:var(--c9-border)] text-[color:var(--c9-text-information)]",
    icon: () => <Info size={16} />,
  },
};

/** Mount once, at the app root. */
export function Toaster(): JSX.Element {
  return (
    <Show when={store.items().length > 0}>
      <Portal>
        <div
          class="pointer-events-none fixed inset-x-0 bottom-0 flex flex-col items-center gap-100 p-300"
          style={{ "z-index": "var(--c9-z-flag)" }}
          role="status"
          aria-live="polite"
        >
          <For each={store.items()}>
            {(item) => (
              <div
                class={cn(
                  "pointer-events-auto flex w-full max-w-[26rem] items-start gap-100 rounded-medium border",
                  "bg-[var(--c9-surface-overlay)] px-200 py-150 shadow-[var(--c9-shadow-overlay)]",
                  TONE[item.tone].class,
                )}
              >
                <span class="mt-025 shrink-0">{TONE[item.tone].icon()}</span>
                <p class="min-w-0 flex-1 text-body text-[color:var(--c9-text)]">{item.message}</p>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => store.dismiss(item.id)}
                  class={cn(
                    "-mr-050 -mt-025 inline-flex size-6 shrink-0 items-center justify-center rounded-[3px]",
                    "text-[color:var(--c9-icon-subtle)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                    focusRing,
                  )}
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </For>
        </div>
      </Portal>
    </Show>
  );
}
